import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'react-toastify';
import { LuPrinter } from 'react-icons/lu';
import Loading from '@/shared/components/Carregando';
import Links from '@/shared/constants/navegacao';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { ValidarPermissaoUsuario } from '@/modules/system/auth/application/validate-permissions';
import { PermissionEnum } from '@/modules/system/auth/domain/enums/permission.enum';
import { RoleEnum } from '@/modules/system/auth/domain/enums/role.enum';
import { imprimirHtml } from '@/shared/utils/impressao';
import type { ReadConselhoSuperiorDto } from '@/api.types';
import { getConselhoSuperiorAtual } from './conselho-superior.service';
import {
  formatarLinhaImpressaoMembro,
  montarHtmlImpressaoConselho,
  rotuloBienio,
} from './conselho-superior.utils';

const COR_INSTITUCIONAL = '#1b5e3b';

function NatoItem({ cargo, nome }: { cargo: string; nome?: string | null }) {
  const nomeFmt = String(nome ?? '').trim().toUpperCase() || '—';
  return (
    <div className="mb-3">
      <div className="fw-bold text-uppercase" style={{ color: COR_INSTITUCIONAL, lineHeight: 1.45 }}>
        {cargo}
      </div>
      <div className="text-uppercase" style={{ color: COR_INSTITUCIONAL, lineHeight: 1.45 }}>
        {nomeFmt}
      </div>
    </div>
  );
}

function MembroLinha({ linha }: { linha: string }) {
  const idx = linha.indexOf(',');
  if (idx < 0) {
    return (
      <div className="mb-2 text-uppercase" style={{ color: COR_INSTITUCIONAL, lineHeight: 1.45 }}>
        {linha}
      </div>
    );
  }
  const cargo = `${linha.slice(0, idx + 1)} `;
  const nome = linha.slice(idx + 1).trimStart();
  return (
    <div className="mb-2 text-uppercase" style={{ color: COR_INSTITUCIONAL, lineHeight: 1.45 }}>
      <strong>{cargo}</strong>
      {nome}
    </div>
  );
}

function Secao({ titulo, destaque, children }: { titulo: string; destaque?: boolean; children: ReactNode }) {
  return (
    <section className="mb-5">
      <h2
        className="text-uppercase fw-bold mb-3"
        style={{
          color: COR_INSTITUCIONAL,
          fontSize: destaque ? '1.25rem' : '1.1rem',
          letterSpacing: '0.02em',
        }}
      >
        {titulo}
      </h2>
      {children}
    </section>
  );
}

export default function ConselhoSuperiorPage() {
  const { setActions } = useBreadcrumbActions();
  const podeEditar = ValidarPermissaoUsuario(RoleEnum.CONSELHO_SUPERIOR, PermissionEnum.UPDATE);
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState<ReadConselhoSuperiorDto | null>(null);

  useEffect(() => {
    const next: Parameters<typeof setActions>[0] = {};
    if (podeEditar) {
      next.primary = {
        label: 'Editar',
        href: Links.ConselhoSuperior_Editar,
        onClick: () => { },
      };
    }
    if (dados?.id) {
      next.print = {
        label: 'Imprimir',
        icon: LuPrinter,
        onClick: () => {
          imprimirHtml(montarHtmlImpressaoConselho(dados));
        },
      };
    }
    setActions(next);
    return () => setActions({});
  }, [podeEditar, setActions, dados]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const atual = await getConselhoSuperiorAtual();
        setDados(atual);
      } catch (error) {
        toast.error(String(error));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const listas = useMemo(() => {
    const membros = dados?.membros ?? [];
    const linhasTipo = (tipo: string, usarPessoa: boolean) =>
      membros
        .filter((m) => m.tipo === tipo)
        .map((m) =>
          formatarLinhaImpressaoMembro(
            usarPessoa
              ? { nome: m.pessoa?.nome, titulo: 'Defensor Público' }
              : { nome: m.defensor?.nome, titulo: m.defensor?.titulo },
          ),
        )
        .filter(Boolean);

    return {
      primeira: linhasTipo('PRIMEIRA_CATEGORIA', false),
      segunda: linhasTipo('SEGUNDA_CATEGORIA', false),
      voz: linhasTipo('PARTICIPANTE_VOZ', true),
    };
  }, [dados]);

  if (loading) return <Loading />;
  if (!dados) return <div className="text-muted">Não foi possível carregar o Conselho Superior.</div>;

  const bienio = rotuloBienio(dados.anoInicio, dados.anoFim);

  return (
    <>
      <div className="d-flex flex-wrap justify-content-end align-items-center gap-2 mb-3">
        <span className="badge text-bg-secondary">Biênio {bienio}</span>
      </div>

      {!dados.id ? (
        <p className="text-muted text-center">Ainda não há composição cadastrada para o biênio atual.</p>
      ) : null}

      <div className="mx-auto py-2" style={{ maxWidth: 720, textAlign: 'center' }}>
        <Secao titulo="MEMBROS NATOS" destaque>
          <NatoItem cargo="DEFENSOR PÚBLICO-GERAL" nome={dados.defensorPublicoGeral?.nome} />
          <NatoItem
            cargo="SUBDEFENSOR PÚBLICO-GERAL PARA ASSUNTOS INSTITUCIONAIS"
            nome={dados.subdefensorInstitucionais?.nome}
          />
          <NatoItem
            cargo="SUBDEFENSOR PÚBLICO-GERAL PARA ASSUNTOS ADMINISTRATIVOS"
            nome={dados.subdefensorAdministrativos?.nome}
          />
          <NatoItem cargo="CORREGEDOR-GERAL/OUVIDOR-GERAL" nome={dados.corregedorOuvidorGeral?.nome} />
        </Secao>

        <Secao titulo={`MEMBROS ELEITOS DE 1ª CATEGORIA – BIÊNIO ${bienio}:`}>
          {listas.primeira.length === 0 ? (
            <div className="fst-italic" style={{ color: COR_INSTITUCIONAL, opacity: 0.7 }}>
              —
            </div>
          ) : (
            listas.primeira.map((linha) => <MembroLinha key={linha} linha={linha} />)
          )}
        </Secao>

        <Secao titulo={`MEMBROS ELEITOS DE 2ª CATEGORIA – BIÊNIO ${bienio}:`}>
          {listas.segunda.length === 0 ? (
            <div className="fst-italic" style={{ color: COR_INSTITUCIONAL, opacity: 0.7 }}>
              —
            </div>
          ) : (
            listas.segunda.map((linha) => <MembroLinha key={linha} linha={linha} />)
          )}
        </Secao>

        <Secao titulo="PARTICIPANTE COM DIREITO A VOZ:">
          {listas.voz.length === 0 ? (
            <div className="fst-italic" style={{ color: COR_INSTITUCIONAL, opacity: 0.7 }}>
              —
            </div>
          ) : (
            listas.voz.map((linha) => <MembroLinha key={linha} linha={linha} />)
          )}
        </Secao>
      </div>
    </>
  );
}
